import { z } from 'zod';
import { MoneySchema } from './common.js';
// Wire shape for Decimal columns — Prisma emits canonical strings; accept
// either string or non-negative number (matches accrual / prepaid / lva).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// Signed counterpart — the net defined-benefit liability = DBO − plan assets goes
// NEGATIVE for a funded/overfunded plan (a defined-benefit asset). Same inferred type.
const SignedDecimalLike = z.union([MoneySchema, z.coerce.number()]);
// ============================================================
// Enums (mirror Prisma)
// ============================================================
export const EmpBenefitSourceSchema = z.enum(['ACTUARIAL_REPORT', 'MANUAL']);
export const EmpBenefitMonthlyStatusSchema = z.enum(['PENDING', 'POSTED', 'REVERSED']);
// ============================================================
// EmpBenefitValuation — read shape
// ============================================================
export const EmpBenefitValuationSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    valuationDate: z.coerce.date(),
    asOfDate: z.coerce.date(),
    totalDbo: DecimalLike,
    plannedAssetsValue: DecimalLike,
    netLiability: SignedDecimalLike,
    discountRate: DecimalLike,
    salaryGrowthRate: DecimalLike,
    employeeCount: z.number().int().nonnegative(),
    source: EmpBenefitSourceSchema,
    reportRef: z.string().nullable().optional(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    // v2 actuarial assumptions (surfaced so the dashboard/actuarial tabs show the
    // REAL values instead of hardcoded placeholders that contradict the disclosure).
    projectedServiceCost: DecimalLike.nullable().optional(),
    openingDbo: DecimalLike.nullable().optional(),
    turnoverRate: DecimalLike.nullable().optional(),
    mortalityTable: z.string().nullable().optional(),
    retirementAge: z.number().int().nullable().optional(),
    weightedAvgDuration: DecimalLike.nullable().optional(),
    // Signed actuarial-report PDF pointer (set by the attachment upload). Surfaced
    // so the actuarial list can offer a "ดาวน์โหลด PDF" link when one is bound.
    pdfStorageKey: z.string().nullable().optional(),
    pdfUploadedAt: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============================================================
// CreateEmpBenefitValuation — POST body
//
// plannedAssetsValue defaults to 0 (Thai PAE companies typically run unfunded
// plans). netLiability is computed server-side as totalDbo − plannedAssetsValue
// — clients don't supply it.
// ============================================================
export const CreateEmpBenefitValuationSchema = z.object({
    valuationDate: z.iso.date(),
    asOfDate: z.iso.date(),
    totalDbo: z.coerce.number().nonnegative(),
    plannedAssetsValue: z.coerce.number().nonnegative().default(0),
    // 0.00 – 99.99 — stored as percent points.
    discountRate: z.coerce.number().min(0).max(99.99),
    salaryGrowthRate: z.coerce.number().min(0).max(99.99),
    employeeCount: z.coerce.number().int().nonnegative(),
    source: EmpBenefitSourceSchema,
    reportRef: z.string().nullable().optional(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
});
// ============================================================
// EmpBenefitMonthlyEntry — read shape
// ============================================================
export const EmpBenefitMonthlyEntrySchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
    valuationId: z.uuid(),
    serviceCost: DecimalLike,
    interestCost: DecimalLike,
    totalAccrual: DecimalLike,
    openingDbo: DecimalLike,
    closingDbo: DecimalLike,
    status: EmpBenefitMonthlyStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    postedAt: z.coerce.date().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row — adds derived valuation context + the engine approval state so the
// periodic tab can drive the propose→submit→approve lifecycle.
export const EmpBenefitMonthlyEntryListRowSchema = EmpBenefitMonthlyEntrySchema.extend({
    valuationDate: z.coerce.date().nullable().optional(),
    reportRef: z.string().nullable().optional(),
    journalVoucherNo: z.string().nullable().optional(),
    approvalStatus: z.enum(['DRAFT', 'PENDING', 'APPROVED', 'REJECTED', 'RETURNED']).optional(),
    currentStep: z.number().int().optional(),
});
// ============================================================
// POST /emp-benefit/monthly/run — { period }
// ============================================================
export const RunEmpBenefitMonthlySchema = z.object({
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
});
export const RunEmpBenefitMonthlyResultSchema = z.object({
    period: z.string(),
    entry: EmpBenefitMonthlyEntrySchema.nullable(),
    alreadyPosted: z.boolean(),
    journalEntryId: z.uuid().nullable(),
    journalVoucherNo: z.string().nullable(),
});
// ============================================================
// TAS 19 phase-1 enums + new entity schemas (settlement / remeasurement /
// valuation v2). Mirror the Prisma enums of the same name.
// ============================================================
export const EmpBenefitPlanTypeSchema = z.enum(['POST_EMPLOYMENT', 'OTHER_LONG_TERM']);
export const EmpBenefitApprovalStatusSchema = z.enum([
    'DRAFT',
    'PENDING',
    'APPROVED',
    'REJECTED',
    'RETURNED',
]);
export const EmpBenefitSettlementReasonSchema = z.enum([
    'INVOLUNTARY',
    'RETIREMENT',
    'FIXED_TERM_END',
    'VOLUNTARY',
]);
export const EmpBenefitSettlementStatusSchema = z.enum(['DRAFT', 'POSTED', 'REVERSED']);
// ============================================================
// Actuary-supplied sensitivity + maturity inputs (IAS 19.145 / 147(c)).
// Entered with the valuation and consumed by the disclosure builder. Tightened
// from the old free `record(string, unknown)` so the entry UI, storage and the
// disclosure renderer all agree on one shape.
// ============================================================
// One assumption's ± impact on the DBO (THB). `plus` = the impact of the
// +anchor shift (+0.5pp for rates / +1yr for mortality), `minus` = the −anchor.
// Exported so the disclosure builder can per-key validate stored blobs (a number
// or partial blob degrades to "no row" rather than a fake +0/−0 disclosure row).
export const EmpBenefitSensitivityDeltaSchema = z.object({
    plus: z.coerce.number(),
    minus: z.coerce.number(),
});
export const EmpBenefitSensitivityInputSchema = z
    .object({
    discountRate: EmpBenefitSensitivityDeltaSchema.optional(),
    salaryGrowth: EmpBenefitSensitivityDeltaSchema.optional(),
    turnover: EmpBenefitSensitivityDeltaSchema.optional(),
    mortality: EmpBenefitSensitivityDeltaSchema.optional(),
})
    .strict();
// IAS 19.147(c) — expected future benefit payments bucketed by maturity band (THB).
export const EmpBenefitMaturityProfileSchema = z
    .object({
    withinOneYear: z.coerce.number().nonnegative(),
    oneToTwoYears: z.coerce.number().nonnegative(),
    twoToFiveYears: z.coerce.number().nonnegative(),
    beyondFiveYears: z.coerce.number().nonnegative(),
})
    .strict();
// ============================================================
// CreateEmpBenefitValuationV2 — superset of the v1 POST body that adds the
// actuarial roll-forward inputs. Keep v1 re-exported for existing callers.
// ============================================================
export const CreateEmpBenefitValuationV2Schema = CreateEmpBenefitValuationSchema.extend({
    planCode: z.string().default('SEV'),
    projectedServiceCost: z.coerce.number().nonnegative().default(0),
    openingDbo: z.coerce.number().nonnegative().default(0),
    pastServiceCost: z.coerce.number().default(0),
    turnoverRate: z.coerce.number().min(0).max(99.99).nullable().optional(),
    mortalityTable: z.string().nullable().optional(),
    retirementAge: z.coerce.number().int().positive().nullable().optional(),
    weightedAvgDuration: z.coerce.number().nonnegative().nullable().optional(),
    sensitivity: EmpBenefitSensitivityInputSchema.nullable().optional(),
    maturityProfile: EmpBenefitMaturityProfileSchema.nullable().optional(),
});
// ============================================================
// CreateEmpBenefitSettlement — POST body for a settlement event.
// isDraft=true marks a future/expected settlement (no JV posted).
// ============================================================
export const CreateEmpBenefitSettlementSchema = z
    .object({
    planCode: z.string().default('SEV'),
    employeeRef: z.string().min(1).max(64),
    employeeName: z.string().min(1).max(200),
    department: z.string().max(200).nullable().optional(),
    position: z.string().max(200).nullable().optional(),
    hireDate: z.iso.date(),
    settlementDate: z.iso.date(),
    finalMonthlyWage: z.coerce.number().positive(),
    carriedProvision: z.coerce.number().nonnegative(),
    reason: EmpBenefitSettlementReasonSchema,
    bankAccountId: z.uuid().nullable().optional(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    isDraft: z.boolean().default(false), // true = future/expected (no JV)
})
    .refine((v) => new Date(v.settlementDate) >= new Date(v.hireDate), {
    message: 'settlementDate must be >= hireDate',
    path: ['settlementDate'],
});
// ============================================================
// RunEmpBenefitRemeasurement — POST body for an OCI remeasurement run.
// ============================================================
export const RunEmpBenefitRemeasurementSchema = z.object({
    valuationId: z.uuid(),
    demographicGainLoss: z.coerce.number().default(0),
    financialGainLoss: z.coerce.number().default(0),
    experienceGainLoss: z.coerce.number().default(0),
});
// ============================================================
// Reversal — POST .../{monthly|settlements|remeasurements}/:id/reverse
// Books the contra-JV into a chosen OPEN period (YYYYMM); never edits the
// original voucher.
// ============================================================
export const ReverseEmpBenefitSchema = z.object({
    reversalPeriod: z.string().regex(/^\d{6}$/, 'reversalPeriod must be YYYYMM'),
});
export const EmpBenefitWhatIfSchema = z.object({
    valuationId: z.uuid(),
    // Deltas in percentage points (discount/salary/turnover) or years (mortality).
    discountDelta: z.coerce.number().optional(),
    salaryDelta: z.coerce.number().optional(),
    turnoverDelta: z.coerce.number().optional(),
    mortalityDelta: z.coerce.number().optional(),
});
//# sourceMappingURL=emp-benefit.js.map